export type ParsedDriver = {
  email: string;
  badge_ids: string[];
};

export type ParsedDriverCsv = {
  drivers: ParsedDriver[];
  hasEmailColumn: boolean;
  hasBadgeColumn: boolean;
  /** Rows skipped because the email is missing, malformed or repeated. */
  skippedRows: { line: number; reason: string }[];
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeHeader = (header: string): string =>
  header
    .trim()
    .toLowerCase()
    .replace(/^["']+|["';\s]+$/g, "");

const unquote = (value: string): string =>
  value
    .trim()
    .replace(/^"(.*)"$/, "$1")
    .trim();

const splitBadgeIds = (raw: string): string[] =>
  raw
    .split(";")
    .map((badgeId) => badgeId.trim())
    .filter(Boolean);

/** Parses a driver CSV with an `email` column (required) and an optional
 * `badge_ids` column holding semicolon-separated badge IDs. */
export const parseDriverCsv = (text: string): ParsedDriverCsv => {
  const lines = text.split(/\r?\n/);
  const headerIndex = lines.findIndex((line) => line.trim());
  const headers = (lines[headerIndex] ?? "").split(",").map(normalizeHeader);
  const emailIdx = headers.indexOf("email");
  const badgeIdx = headers.indexOf("badge_ids");
  const result: ParsedDriverCsv = {
    drivers: [],
    hasEmailColumn: emailIdx !== -1,
    hasBadgeColumn: badgeIdx !== -1,
    skippedRows: [],
  };
  if (emailIdx === -1) {
    return result;
  }

  const seen = new Set<string>();
  lines.slice(headerIndex + 1).forEach((line, idx) => {
    if (!line.trim()) {
      return;
    }
    const lineNumber = headerIndex + idx + 2;
    const values = line.split(",").map(unquote);
    const email = (values[emailIdx] ?? "").toLowerCase();
    if (!EMAIL_PATTERN.test(email)) {
      result.skippedRows.push({
        line: lineNumber,
        reason: email ? `invalid email "${email}"` : "missing email",
      });
      return;
    }
    if (seen.has(email)) {
      result.skippedRows.push({
        line: lineNumber,
        reason: `duplicate email "${email}"`,
      });
      return;
    }
    seen.add(email);
    result.drivers.push({
      email,
      badge_ids: badgeIdx === -1 ? [] : splitBadgeIds(values[badgeIdx] ?? ""),
    });
  });

  return result;
};
